/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type { TFlashMessageType } from 'volta/models/bv-flash';
import type BvFlashInstance from 'volta/models/bv-flash';
import type BvFlashService from 'volta/services/bv-flash';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

const TYPES: TFlashMessageType[] = ['info', 'success', 'warning', 'error'];

interface BvFlashSignature {
  Args: {
    flash: BvFlashInstance;
  };
  Element: HTMLDivElement;
}

export default class BvFlashComponent extends Component<BvFlashSignature> {
  @service declare bvFlash: BvFlashService;

  @tracked paused = false;

  // Set icon depending on notification type
  get flashIcon() {
    const { flash } = this.args;

    switch (flash.type) {
      case 'info': {
        return 'information';
      }
      case 'success': {
        return 'checkmark';
      }
      case 'warning': {
        return 'alert';
      }
      case 'error': {
        return 'close';
      }
    }
  }

  get iconColor() {
    return this.args.flash.type === 'error' ? 'danger' : this.args.flash.type;
  }

  get processedType() {
    const { flash } = this.args;
    return flash && flash.type && TYPES.includes(flash.type)
      ? `bv-flash--${flash.type}`
      : undefined;
  }

  @action
  handleRemoveFlash() {
    this.bvFlash.remove(this.args.flash);
  }

  @action
  handleMouseDown(_e: MouseEvent) {
    const { flash } = this.args;
    flash.onClick?.(flash);
  }

  @action
  handleMouseEnter() {
    const { flash } = this.args;
    if (flash.autoClear) {
      this.paused = true;
      this.bvFlash.pauseAutoClear(flash);
    }
  }

  @action
  handleMouseLeave() {
    const { flash } = this.args;
    if (flash.autoClear) {
      this.paused = false;
      this.bvFlash.setupAutoClear(flash);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvFlash: typeof BvFlashComponent;
    'bv-flash': typeof BvFlashComponent;
  }
}
